import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { RxIntuitivePatientHeaderComponent } from '@modules/rx-intuitive-patient-header/containers/rx-intuitive-patient-header/rx-intuitive-patient-header.component';
import { RxBannerDismissibleComponent } from '@shared/components/rx-banner-dismissible/rx-banner-dismissible.component';
import { RxIntuitiveOrderLayoutComponent } from '@shared/components/rx-intuitive-order-layout/rx-intuitive-order-layout.component';
import { RxProcedureButtonComponent } from '@shared/components/rx-procedure-button/rx-procedure-button.component';
import { FavoriteSelectorComponent } from '@shared/components/favorite-selector/favorite-selector.component';
import { SaveAndContinueLaterDialogService } from '@shared/components/save-and-continue-later-dialog/save-and-continue-later-dialog.service';
import { LabBrokerUnavailableDialogService, DentiraUnavailableResult } from '@shared/components/dentira-unavailable-dialog';
import { SendToOption } from '@shared/models/send-to-option';
import { LabBrokerViewContainerFacade } from './lab-broker-view-container.facade';
import { EMPTY, Observable, switchMap, timer, ReplaySubject, of, tap, combineLatest } from 'rxjs';
import {
	LAB_BROKER_UNAVAILABLE_DIALOG_FIRST_RETRY_MS,
	LAB_BROKER_UNAVAILABLE_DIALOG_RETRY_INTERVAL_MS
} from '@modules/lab-broker-view/consts';
import { filter, map, take, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { LabBrokerIframeComponent } from '../../components/lab-broker-iframe/lab-broker-iframe.component';
import { LabBrokerFooterComponent } from '../../components/lab-broker-footer/lab-broker-footer.component';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { CustomRxBiDialog, CustomRxBiDialogOption } from '../../utils/custom-rx-bi-events';
import { LabBrokerCloseService } from "@modules/lab-broker-view/services/lab-broker-close.service";

@Component({
	selector: 'rx-lab-broker-view-container',
	templateUrl: './lab-broker-view-container.component.html',
	styleUrls: ['./lab-broker-view-container.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LabBrokerViewContainerFacade, RxIntuitiveIndicationMapperService],
	imports: [
		AsyncPipe,
		NgIf,
		ReactiveFormsModule,
		TranslateModule,
		TlkButtonComponent,
		RxIntuitivePatientHeaderComponent,
		RxBannerDismissibleComponent,
		RxIntuitiveOrderLayoutComponent,
		RxProcedureButtonComponent,
		FavoriteSelectorComponent,
		LabBrokerIframeComponent,
		LabBrokerFooterComponent
	]
})
export class LabBrokerViewContainerComponent extends BaseDestroyableComponent implements OnInit, OnDestroy, AfterViewInit {
	@Output() readonly labBrokerCaseConfirmed = new EventEmitter<void>();

	@ViewChild(LabBrokerIframeComponent) private readonly labBrokerIframeComponent: LabBrokerIframeComponent;

	readonly viewState$ = this.facade.viewState$;
	readonly isActive$ = this.facade.isActive$;
	readonly isLoading$ = combineLatest([this.facade.isLoading$, this.facade.isClosing$]).pipe(map(([isLoading, isClosing]) => isLoading || isClosing));
	readonly isCompleted$ = this.facade.isCompleted$;
	readonly isSaveAndContinueLaterButtonVisible$ = this.facade.isSaveAndContinueLaterButtonVisible$;
	readonly isError$ = this.facade.isError$;
	readonly errorMessage$ = this.facade.errorMessage$;
	readonly iframeUrl$ = this.facade.iframeUrl$;
	readonly integratorName$ = this.facade.integratorName$;
	readonly selectedProcedure$ = this.facade.selectedProcedure$;

	readonly isFooterVisible$ = this.facade.isFooterVisible$;

	readonly sendToControl = new FormControl<SendToOption | null>(null);

	readonly sendToOptions$ = this.facade.sendTo$.pipe(map(sendTo => (sendTo ? [sendTo] : [])));

	private readonly iframeNativeElement$ = new ReplaySubject<HTMLIFrameElement>();

	constructor(
		private readonly facade: LabBrokerViewContainerFacade,
		private readonly saveAndContinueLaterDialogService: SaveAndContinueLaterDialogService,
		private readonly dentiraUnavailableDialogService: LabBrokerUnavailableDialogService,
		private readonly labBrokerCloseService: LabBrokerCloseService
	) {
		super();
	}

	ngOnInit(): void {
		this.facade.setPatientHeaderReadonly();

		this.facade.sendTo$
			.pipe(takeUntil(this.componentAlive$))
			.subscribe(value => this.sendToControl.setValue(value, { emitEvent: false }));

		const token$ = this.facade.getToken();

		this.facade.initCommunication(this.iframeNativeElement$, token$).pipe(takeUntil(this.componentAlive$)).subscribe();

		this.facade.isCompleted$
			.pipe(
				filter(isCompleted => isCompleted),
				take(1),
				takeUntil(this.componentAlive$)
			)
			.subscribe(() => this.onSendToLabClicked());

		this.facade.handleCloseRequests().pipe(takeUntil(this.componentAlive$)).subscribe();

		this.facade.isUnavailable$
			.pipe(
				distinctUntilChanged(),
				filter(unavailable => unavailable && !this.facade.isDialogSuppressed),
				switchMap(() => this.showDialogWithRetry()),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	ngAfterViewInit(): void {
		this.iframeNativeElement$.next(this.labBrokerIframeComponent?.iframe);
		this.iframeNativeElement$.complete();
	}

	ngOnDestroy(): void {
		this.facade.teardownCommunication();
		this.facade.resetPatientHeaderReadonly();
		this.dentiraUnavailableDialogService.closeIfOpen();
		super.ngOnDestroy();
	}

	onReturnToSummary(): void {
		this.facade.navigateAway();
	}

	onSendToLabClicked(): void {
		this.labBrokerCaseConfirmed.emit();
	}

	onSaveAndContinueLater(): void {
		this.saveAndContinueLaterDialogService
			.open(this.facade.integratorName ?? '')
			.pipe(
				switchMap(completeOnLabBrokerSite => {
					if (completeOnLabBrokerSite !== null) {
						this.facade.trackDialogConfirm(
							CustomRxBiDialog.SaveAndContinueLater,
							completeOnLabBrokerSite
								? CustomRxBiDialogOption.CompleteAndSubmitToPartner
								: CustomRxBiDialogOption.ContinueOnScannerLater
						);
					}

					if (completeOnLabBrokerSite === true) {
						return this.labBrokerCloseService.initiateClose('FALLBACK').pipe(
							tap(() => {
								this.facade.teardownCommunication();
								this.facade.bypassLabBrokerSendStep();
								this.labBrokerCaseConfirmed.emit();
							})
						);
					}

					return of(undefined);
				}),
				takeUntil(this.componentAlive$))
			.subscribe();
	}

	// ---------------------------------------------------------------------------
	// Dentira unavailability — retry/dialog chain
	// ---------------------------------------------------------------------------

	private showDialogWithRetry(): Observable<void> {
		return this.dentiraUnavailableDialogService.open(this.facade.integratorName ?? '').pipe(
			switchMap(result => {
				if (result === null) {
					// First cancel: wait 10s then enter the 30s loop
					return timer(LAB_BROKER_UNAVAILABLE_DIALOG_FIRST_RETRY_MS).pipe(switchMap(() => this.retryLoop()));
				}

				return this.handleDialogResult(result);
			})
		);
	}

	private retryLoop(): Observable<void> {
		if (!this.facade.isUnavailable || this.facade.isDialogSuppressed) {
			return EMPTY;
		}

		return this.dentiraUnavailableDialogService.open(this.facade.integratorName ?? '').pipe(
			switchMap(result => {
				if (result === null) {
					// Subsequent cancels: 30s cadence
					return timer(LAB_BROKER_UNAVAILABLE_DIALOG_RETRY_INTERVAL_MS).pipe(switchMap(() => this.retryLoop()));
				}

				return this.handleDialogResult(result);
			})
		);
	}

	private handleDialogResult(result: DentiraUnavailableResult): Observable<void> {
		if (result === 'tryAgainLater') {
			// Navigate back to RxForm with "Continue to Dentira" visible; no suppression
			this.facade.trackDialogConfirm(CustomRxBiDialog.NotResponding, CustomRxBiDialogOption.TryAgainLater);
			this.facade.teardownCommunication();
		} else if (result === 'completeOnLabBroker') {
			// Suppress future auto-show, tear down iframe, bypass "Send to Lab" step
			this.facade.trackDialogConfirm(CustomRxBiDialog.NotResponding, CustomRxBiDialogOption.CompleteAndSubmitToPartner);
			this.facade.teardownCommunication();
			this.facade.suppressUnavailableDialog();
			this.facade.bypassLabBrokerSendStep();
			this.labBrokerCaseConfirmed.emit();
		}

		return EMPTY;
	}
}
